/**
 * The rules every "Export PDF" dialog follows (the meet printout and the
 * competition PDFs alike), so the gate lives in one place
 * (owner decision, 7 Oct 2026; components/competitions/ExportPdfDialog.vue):
 *
 *   - Print and Download wait for the permission tick, and for something to
 *     act on;
 *   - nothing that could be printed or saved reaches the page before that
 *     tick. A PDF shown in the browser carries the browser's own Download and
 *     Print, which go round the tick, so a preview is page images (or the
 *     meet's own HTML sheet), never the PDF. The PDF is fetched only when
 *     Print or Download is pressed with the tick given.
 *
 * Imported explicitly (house style), never relied on as an auto import.
 */

/** Print and Download need the permission tick and something ready to act on. */
export const exportPdfCanAct = (ticked, ready) => ticked === true && ready === true;

/**
 * Run Print or Download: refused unless the tick is given and the dialog is
 * ready, and only then is the PDF fetched and handed on. Returns whether it ran.
 */
export const runExportPdfAction = async ({ticked, ready, fetchPdf, deliver}) => {
  if (!exportPdfCanAct(ticked, ready)) return false;
  const file = await fetchPdf();
  await deliver(file);
  return true;
};

/**
 * Print a PDF the admin has just asked for (after the tick), through a frame
 * that is never on screen and is removed again once printed. A browser that
 * will not print a PDF frame opens it instead, so its own viewer can.
 */
export const printPdfBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = 'PDF to print';
  Object.assign(frame.style, {position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0'});
  const cleanUp = () => {
    frame.remove();
    URL.revokeObjectURL(url);
  };
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      // The print window holds its own copy; give it time, then let go.
      setTimeout(cleanUp, 60_000);
    } catch {
      window.open(url, '_blank');
      setTimeout(cleanUp, 60_000);
    }
  };
  frame.src = url;
  document.body.appendChild(frame);
};
